import { useState, useRef, useCallback } from "react";
import { Upload, X, Loader2, Image as ImageIcon, Plus } from "lucide-react";
import { Button } from "../ui/Button";
import { cn } from "../../lib/utils";
import { supabase } from "../../lib/supabase";

interface ImageGalleryInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  maxImages?: number;
  className?: string;
  disabled?: boolean;
}

export function ImageGalleryInput({
  value = [],
  onChange,
  maxImages = 10,
  className,
  disabled = false,
}: ImageGalleryInputProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFile = async (file: File): Promise<string | null> => {
    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Il file deve essere un\'immagine');
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('L\'immagine non può superare i 5MB');
      }

      // Generate unique filename
      const ext = file.name.split('.').pop() || 'jpg';
      const filename = `${Date.now()}-${crypto.randomUUID().substring(0, 8)}.${ext}`;
      const path = `package-items/${filename}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('package-images')
        .upload(path, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('package-images')
        .getPublicUrl(path);

      return publicUrl;
    } catch (err) {
      console.error('Upload error:', err);
      throw err;
    }
  };

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    if (disabled) return;

    const fileArray = Array.from(files);
    const availableSlots = maxImages - value.length;

    if (availableSlots <= 0) {
      setError(`Massimo ${maxImages} immagini consentite`);
      return;
    }

    const filesToUpload = fileArray.slice(0, availableSlots);
    if (filesToUpload.length < fileArray.length) {
      setError(`Puoi caricare ancora ${availableSlots} immagine/i`);
    } else {
      setError(null);
    }

    setIsUploading(true);
    setUploadProgress(`Caricamento 0/${filesToUpload.length}...`);

    const newUrls: string[] = [];

    for (let i = 0; i < filesToUpload.length; i++) {
      setUploadProgress(`Caricamento ${i + 1}/${filesToUpload.length}...`);
      try {
        const url = await uploadFile(filesToUpload[i]);
        if (url) {
          newUrls.push(url);
        }
      } catch (err) {
        console.error('Failed to upload file:', err);
        setError(err instanceof Error ? err.message : 'Errore durante il caricamento');
      }
    }

    if (newUrls.length > 0) {
      onChange([...value, ...newUrls]);
    }

    setIsUploading(false);
    setUploadProgress(null);
  }, [value, onChange, maxImages, disabled]);

  const handleRemove = async (index: number) => {
    if (disabled) return;

    const urlToRemove = value[index];
    const newValue = [...value];
    newValue.splice(index, 1);
    onChange(newValue);

    // Try to delete from storage (optional, won't fail if it doesn't work)
    try {
      const pathMatch = urlToRemove.match(/package-images\/(.+)$/);
      if (pathMatch) {
        await supabase.storage
          .from('package-images')
          .remove([pathMatch[1]]);
      }
    } catch (err) {
      console.error('Failed to delete file from storage:', err);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (!disabled && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const canAddMore = value.length < maxImages && !disabled && !isUploading;

  return (
    <div className={cn("space-y-3", className)}>
      {/* Upload area */}
      {canAddMore && (
        <div
          className={cn(
            "relative border-2 border-dashed rounded-lg p-4 transition-colors",
            isDragging
              ? "border-[#284899] bg-[#284899]/5"
              : "border-border hover:border-[#284899]/50",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleInputChange}
            className="hidden"
            disabled={disabled || isUploading}
          />

          <div className="flex flex-col items-center justify-center py-2">
            {isUploading ? (
              <>
                <Loader2 className="h-8 w-8 animate-spin text-[#284899] mb-2" />
                <p className="text-sm text-muted-foreground">{uploadProgress}</p>
              </>
            ) : (
              <>
                <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm text-center text-muted-foreground mb-2">
                  Trascina le immagini qui oppure
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Seleziona file
                </Button>
                <p className="text-sm text-muted-foreground mt-2">
                  Max {maxImages} immagini, 5MB ciascuna
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}

      {/* Image grid */}
      {value.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {value.map((url, index) => (
            <div
              key={`${url}-${index}`}
              className="relative aspect-square rounded-lg overflow-hidden border border-border group"
            >
              <img
                src={url}
                alt={`Immagine ${index + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Show placeholder on error
                  (e.target as HTMLImageElement).src = '';
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).parentElement!.classList.add('bg-secondary');
                }}
              />
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
              <div className="absolute inset-0 flex items-center justify-center bg-secondary pointer-events-none">
                <ImageIcon className="h-6 w-6 text-muted-foreground" />
              </div>
            </div>
          ))}

          {/* Add more button in grid */}
          {canAddMore && value.length > 0 && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="aspect-square rounded-lg border-2 border-dashed border-border hover:border-[#284899]/50 flex items-center justify-center transition-colors"
            >
              <Plus className="h-6 w-6 text-muted-foreground" />
            </button>
          )}
        </div>
      )}

      {/* Counter */}
      {value.length > 0 && (
        <p className="text-sm text-muted-foreground text-right">
          {value.length}/{maxImages} immagini
        </p>
      )}
    </div>
  );
}
